import { LeaveAreaCode, PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import {
  CalendarDaysIcon,
  CalendarPlusIcon,
  ClipboardListIcon,
  InboxIcon,
  LayoutDashboardIcon,
  UsersIcon,
} from 'lucide-react';

import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ProductSideNav } from '@/platform/components/product-side-nav';
import { registerProductNav } from '@/platform/lib/product-nav';
import { canAccessLeaveAdmin } from '@/products/leave/features/leave-admin-access';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import {
  LEAVE_ADMIN_BALANCES_PATH,
  LEAVE_ADMIN_EXPORT_PATH,
  LEAVE_ADMIN_PATH,
  LEAVE_CALENDAR_PATH,
  LEAVE_COLLEAGUES_PATH,
  LEAVE_DELEGATE_PATH,
  LEAVE_INBOX_PATH,
  LEAVE_REQUEST_PATH,
  LEAVE_REQUESTS_PATH,
  LEAVE_ROOT,
} from '@/products/leave/leave-paths';

const iconClass = 'size-4';

function LeaveNav() {
  const me = useMe();
  const areas = useLeaveAreas();
  const adminPath = adminEntryPath(me.data);
  const items = [
    {
      to: LEAVE_ROOT,
      label: 'Übersicht',
      end: true,
      icon: <LayoutDashboardIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    {
      to: LEAVE_REQUEST_PATH,
      label: 'Beantragen',
      icon: <CalendarPlusIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    {
      to: LEAVE_REQUESTS_PATH,
      label: 'Anträge',
      icon: <ClipboardListIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    {
      to: LEAVE_INBOX_PATH,
      label: 'Eingang',
      icon: <InboxIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    ...(leaveAreaEnabled(areas.data, LeaveAreaCode.CALENDAR) === false
      ? []
      : [
          {
            to: LEAVE_CALENDAR_PATH,
            label: 'Kalender',
            icon: <CalendarDaysIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]),
    {
      to: LEAVE_COLLEAGUES_PATH,
      label: 'Kolleg:innen',
      icon: <UsersIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    ...(leaveAreaEnabled(areas.data, LeaveAreaCode.DELEGATES) === false
      ? []
      : [{ to: LEAVE_DELEGATE_PATH, label: 'Vertretung' }]),
    ...(adminPath ? [{ to: adminPath, label: 'Verwaltung' }] : []),
  ];

  return <ProductSideNav ariaLabel="Urlaubsverwaltung" items={items} />;
}

function adminEntryPath(user: Parameters<typeof hasPermission>[0]): string | null {
  if (!canAccessLeaveAdmin(user)) {
    return null;
  }
  if (hasPermission(user, PermissionCode.LEAVE_POLICY_MANAGE)) {
    return LEAVE_ADMIN_PATH;
  }
  if (hasPermission(user, PermissionCode.LEAVE_BALANCES_ADJUST)) {
    return LEAVE_ADMIN_BALANCES_PATH;
  }
  if (hasPermission(user, PermissionCode.LEAVE_REPORTS_EXPORT)) {
    return LEAVE_ADMIN_EXPORT_PATH;
  }
  return null;
}

registerProductNav({
  code: ProductCode.LEAVE,
  Nav: LeaveNav,
  menuTiles: [
    { id: 'leave-home', label: 'Übersicht', to: LEAVE_ROOT },
    {
      id: 'leave-request',
      label: 'Beantragen',
      to: LEAVE_REQUEST_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.LEAVE_REQUESTS_CREATE),
    },
    {
      id: 'leave-requests',
      label: 'Anträge',
      to: LEAVE_REQUESTS_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.LEAVE_REQUESTS_CREATE) ||
        hasPermission(user, PermissionCode.LEAVE_BALANCE_READ),
    },
    {
      id: 'leave-inbox',
      label: 'Eingang',
      to: LEAVE_INBOX_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.LEAVE_REQUESTS_DECIDE_ALL),
    },
    {
      id: 'leave-calendar',
      label: 'Kalender',
      to: LEAVE_CALENDAR_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.LEAVE_CALENDAR_READ),
    },
    {
      id: 'leave-colleagues',
      label: 'Kolleg:innen',
      to: LEAVE_COLLEAGUES_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.LEAVE_CALENDAR_READ) ||
        hasPermission(user, PermissionCode.LEAVE_BALANCE_READ),
    },
    {
      id: 'leave-delegate',
      label: 'Vertretung',
      to: LEAVE_DELEGATE_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.LEAVE_REQUESTS_CREATE),
    },
    {
      id: 'leave-admin',
      label: 'Verwaltung',
      to: LEAVE_ADMIN_PATH,
      isVisible: canAccessLeaveAdmin,
    },
  ],
  mobileSlots: [
    { kind: 'link', id: 'leave-home', label: 'Übersicht', to: LEAVE_ROOT },
    { kind: 'link', id: 'leave-requests', label: 'Anträge', to: LEAVE_REQUESTS_PATH },
    {
      kind: 'link',
      id: 'leave-request',
      label: 'Beantragen',
      to: LEAVE_REQUEST_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.LEAVE_REQUESTS_CREATE),
    },
  ],
});
